import { useQuery } from '@tanstack/react-query';
import {
  deriveOfflineReadiness,
  type MediaAsset,
  type OfflineReadiness,
  type Screen,
} from '@ghostscreen/contracts';
import { api } from '../../lib/api';

export type ScreenOfflineReadinessState = {
  readiness: OfflineReadiness | null;
  isPending: boolean;
  /** True when the screen has no assigned + published playlist to measure. */
  unavailable: boolean;
};

/**
 * Content offline readiness for the screen's assigned live playlist version.
 * Shares React Query keys with display preview so cards do not double-fetch.
 */
export function useScreenOfflineReadiness(
  screen: Screen,
  organisationId: string,
): ScreenOfflineReadinessState {
  const unpaired = screen.deviceId == null;

  const assignmentQuery = useQuery({
    queryKey: ['screen-assignment', organisationId, screen.id],
    queryFn: () => api.getScreenAssignment(organisationId, screen.id),
    enabled: !unpaired,
  });

  const playlistId = assignmentQuery.data?.playlistId ?? null;
  const desiredVersion = screen.desiredPlaylistVersion;

  const liveQuery = useQuery({
    queryKey: ['playlist-version', playlistId, desiredVersion],
    queryFn: () => api.getPlaylistVersion(organisationId, playlistId!, desiredVersion!),
    enabled: Boolean(!unpaired && playlistId && desiredVersion && desiredVersion > 0),
  });

  // Optional enrichment — mimeType alone is enough to classify today's image/video.
  const mediaQuery = useQuery({
    queryKey: ['media', organisationId],
    queryFn: () => api.listMediaAssets(organisationId),
    enabled: !unpaired && Boolean(playlistId),
  });

  if (unpaired) {
    return { readiness: null, isPending: false, unavailable: true };
  }

  const awaitingLive =
    Boolean(playlistId && desiredVersion && desiredVersion > 0) && liveQuery.isPending;
  const isPending = assignmentQuery.isPending || awaitingLive;

  if (!playlistId || !desiredVersion || desiredVersion <= 0) {
    return { readiness: null, isPending, unavailable: !isPending };
  }

  if (!liveQuery.data) {
    return { readiness: null, isPending, unavailable: !isPending };
  }

  const mediaById = new Map<string, MediaAsset>(
    (mediaQuery.data ?? []).map((asset) => [asset.id, asset]),
  );

  const readiness = deriveOfflineReadiness(
    liveQuery.data.items.map((item) => {
      const asset = mediaById.get(item.assetId);
      return {
        id: item.assetId,
        mimeType: item.mimeType,
        ...(asset?.kind ? { kind: asset.kind } : {}),
        ...(asset?.originalFilename ? { label: asset.originalFilename } : {}),
      };
    }),
  );

  return {
    readiness,
    isPending: false,
    unavailable: readiness == null,
  };
}
